import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useFormik } from "formik";
import { signinSchema } from '../../utils/formSchema';
import { publicRequest } from '../../utils/requestMethods';
import Cookies from 'js-cookie';
import './css/default.css'
import './css/style.css'
import { ButtonLoading, PasswordInput } from '../../components';
import logo from "../../data/logo/logo.png";
import circle from './img/circle.png';
import dot from './img/dot.png';
import flower from './img/flower.png';
import man1 from './img/man-1.png';
import man2 from './img/man-2.png';
import signup from './img/sign-up.png';
import zigzag from './img/zigzag.png';
import { useStateContext } from '../../contexts/ContextProvider';
import ErrorHandler from '../../utils/errorHandler';

const SignIn = () => {
   const { user, school, selectDefaultSchool } = useStateContext();
   const [loading, setLoading] = useState(false);
   const history = useHistory();
   const location = useLocation();

   const {
      values,
      errors,
      touched,
      handleBlur,
      handleChange,
      handleSubmit,
      setFieldError
   } = useFormik({
      initialValues: {
         email: "",
         password: "",
      },
      validationSchema: signinSchema,
      onSubmit: (values) => {
         loginHandler(values);
      },
   });

   useEffect(() => {
      // If user is already authenticated, redirect to home or attempted URL
      if (Cookies.get('accessToken')) {
         const from = location.state?.from?.pathname || '/';
         history.replace(from);
      }
   }, [location, history]);

   const loginHandler = async (values) => {
      setLoading(true);
      
      try {
         const { data } = await publicRequest.post("auth/school/login", values);

         const expireTime = new Date(new Date().getTime() + 5 * 60 * 60 * 1000);
         Cookies.set('accessToken', data.access_token, { 
            secure: true, 
            sameSite: 'strict', 
            expires: expireTime 
         });

         const updatedData = { ...data };
         delete updatedData.access_token;
         localStorage.setItem("tokenWithUser", JSON.stringify(updatedData));
         
         // Set the default school ID immediately after login
         if (updatedData.schools?.schools?.length > 0) {
           const schools = updatedData.schools.schools;
           
           // First, try to find a school marked as current
           let selectedSchool = schools.find(s => s.is_current === true);
           
           // If no current school, try to find the primary school
           if (!selectedSchool) {
             selectedSchool = schools.find(s => s.is_primary === true);
           }
           
           // If still no primary/current school, fallback to first school
           if (!selectedSchool) {
             selectedSchool = schools[0];
           }
           
           localStorage.setItem('selectedSchoolId', selectedSchool._id);
           
           // Also trigger school selection in context if available
           if (selectDefaultSchool) {
             selectDefaultSchool();
           }
         }
         
         // Use window.location for full page reload to ensure proper sidebar loading
         if (updatedData.role === 1) { // Teacher
            window.location.href = "/teacher";
         } else { // Admin, Burser, Academic Admin (role 3), or any other school-side role
            window.location.href = "/";
         }
      } catch (error) {
         // Use centralized error handler for all errors
         ErrorHandler.handleError(error, {
            showToast: true,
            logToConsole: true,
            onValidationError: (fieldErrors) => {
               // Handle validation errors by setting them in the form
               Object.entries(fieldErrors).forEach(([field, message]) => {
                  setFieldError(field, message);
               });
            }
         });
      } finally {
         setLoading(false);
      }
   };

    return (
      <main>
          <section className="signup__area po-rel-z1 p-t-40 pb-100">
            <div className="sign__shape">
               <img className="man-1" src={man1} alt="" />
               <img className="man-2" src={man2} alt="" />
               <img className="circle" src={circle} alt="" />
               <img className="zigzag" src={zigzag} alt="" />
               <img className="dot" src={dot} alt="" />
               <img className="bg" src={signup} alt="" />
               <img className="flower" src={flower} alt="" />
            </div>
            <div className="row">
               <div className="col-xxl-8 offset-xxl-2 col-xl-8 offset-xl-2">
                  <div className="section__title-wrapper text-center m-b-40">
                     <img src={logo} alt="logo" className="mx-auto" width="250" />
                  </div>
               </div>
            </div>
            <div className="max-w-xl mx-auto mt-3 divide-y p-3">
               <div className="sign__wrapper bg-white mx-auto rounded-3xl">
                  <div className="sign__header mb-35">
                     <div className="sign__in text-center">
                        <h3 className="text-2xl font-bold section__title">Sign into your Account.</h3>
                     </div>
                  </div>
                  <div className="sign__form px-2">
                     <form onSubmit={handleSubmit}>
                        <div className="sign__input-wrapper mb-25">
                           <h5>Work email</h5>
                           <div>
                              <div className="sign__input relative">
                                 <input 
                                    type="email" 
                                    id="email" 
                                    placeholder="Email address" 
                                    value={values.email} 
                                    onBlur={handleBlur} 
                                    onChange={handleChange} 
                                    required
                                    className={errors.email && touched.email ? 'border-red-500' : ''}
                                 />
                                 <i className="fal fa-envelope"></i>
                              </div>
                              {errors.email && touched.email &&
                                 <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                                    <span className="font-medium">Oops!</span> {errors.email}
                                 </p>
                              }
                           </div>
                        </div>
                        <div className="sign__input-wrapper mb-6">
                           <div className="sign__input">
                              <PasswordInput
                                 id="password"
                                 label="Password"
                                 placeholder="Password"
                                 value={values.password}
                                 onChange={handleChange}
                                 onBlur={handleBlur}
                                 errorMessage={errors.password && touched.password ? errors.password : ""}
                              />
                           </div>
                        </div>
                        <div className="sign__action d-sm-flex justify-content-between mb-30">
                           <div className="sign__agree d-flex align-items-center pr-2">
                              <input className="m-check-input" type="checkbox" id="m-agree" />
                              <label className="m-check-label" htmlFor="m-agree">Keep me signed in</label>
                           </div>
                           <div className="sign__forgot">
                              <Link to="/auth/reset">Forgot password?</Link>
                           </div>
                        </div>
                        {loading ? (
                           <ButtonLoading styles="e-btn w-100 auth-btn" />
                        ) : (
                           <button type="submit" className="e-btn w-100 auth-btn">
                              <span></span> Sign In
                           </button>
                        )}
                        <div className="sign__new text-center mt-10">
                           <p>New to Acceede? <Link to="/signup">Sign Up</Link></p>
                        </div>
                     </form>
                  </div>
               </div>
            </div>
         </section>
      </main> 
    );
};

export default SignIn;
